import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowsClockwise, Plus, Trash } from '@phosphor-icons/react';
import {
  getListCompetitorPostsQueryKey,
  getListCompetitorsQueryKey,
  useListCompetitors,
  useRefreshCompetitor,
  useReplaceCompetitors,
} from '@reelplicate/api-client/competitors';
import type { CompetitorAccountDto } from '@reelplicate/api-client/model';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import { Button, Field, IconButton, Inline, Stack, toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Link as RouterLink } from 'react-router';

import { AccountsEditor } from '@/features/competitors/accounts-editor';
import {
  accountsUsed,
  blankCompetitor,
  CompetitorsFormSchema,
  type CompetitorsFormValues,
  toCompetitorInputs,
  toCompetitorValues,
} from '@/features/competitors/competitor-form';
import { views } from '@/features/competitors/format';
import { SiteInput } from '@/features/onboarding/favicon';

import { SettingsCard, SettingsPageLayout, SettingsSection } from './settings-layout';
import { UsageMeter } from './usage-meter';

/** Where tracking an account has got to, in a line. */
function accountStatus(account: CompetitorAccountDto) {
  if (account.status === 'NOT_FOUND') {
    return { tone: 'danger', text: 'Not found - check the handle' } as const;
  }
  if (account.status === 'ERROR') {
    return {
      tone: 'warning',
      text: account.lastError ?? 'The last check failed',
    } as const;
  }
  if (!account.postsSyncedAt) {
    return { tone: 'neutral', text: 'Reading their posts…' } as const;
  }
  const parts = [
    account.followers !== null && `${views(account.followers)} followers`,
    account.medianViews !== null
      ? `usually ${views(account.medianViews)} views`
      : 'too few posts to know what’s usual yet',
  ].filter(Boolean);
  const text = parts.join(' · ');
  return { tone: 'neutral', text: text.charAt(0).toUpperCase() + text.slice(1) } as const;
}

const TONE = {
  neutral: 'text-text-secondary',
  warning: 'text-status-warning-fg',
  danger: 'text-status-danger-fg',
} as const;

/**
 * Settings → Competitors: each competitor's site and the TikTok / Instagram
 * accounts it runs - as many as it has - which we check daily for posts
 * beating their usual views, how each account is doing, and how many
 * accounts the plan tracks.
 */
export function CompetitorsSettingsPage() {
  const queryClient = useQueryClient();
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id ?? '';
  const { data } = useListCompetitors(workspaceId, {
    query: { enabled: Boolean(workspaceId) },
  });
  const replace = useReplaceCompetitors();
  const refresh = useRefreshCompetitor();
  const form = useForm<CompetitorsFormValues>({
    resolver: zodResolver(CompetitorsFormSchema),
    defaultValues: { competitors: [] },
  });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'competitors',
  });
  const values = useWatch({ control: form.control, name: 'competitors' });

  useEffect(() => {
    if (data && !form.formState.isDirty) {
      form.reset({ competitors: toCompetitorValues(data.items) });
    }
  }, [data, form]);

  const onSubmit = form.handleSubmit(async ({ competitors }) => {
    let saved;
    try {
      saved = await replace.mutateAsync({
        id: workspaceId,
        data: { competitors: toCompetitorInputs(competitors) },
      });
    } catch {
      return; // Shown as a toast.
    }
    form.reset({ competitors: toCompetitorValues(saved.items) });
    queryClient.setQueryData(getListCompetitorsQueryKey(workspaceId), saved);
    await queryClient.invalidateQueries({
      queryKey: getListCompetitorPostsQueryKey(workspaceId),
    });
    toast.success('Competitors saved');
  });

  const limit = data?.limit ?? 3;
  const used = accountsUsed(values ?? []);
  const full = used >= limit;
  /** The saved competitor for a row (unsaved rows have none). */
  const savedFor = (id: string | undefined) =>
    id ? data?.items.find((c) => c.id === id) : undefined;

  return (
    <SettingsPageLayout
      title="Competitors"
      description="Whose posts we watch. Their TikTok and Instagram accounts are checked daily for posts beating their usual views."
      onSubmit={onSubmit}
      actions={
        <Button
          type="submit"
          disabled={!form.formState.isDirty}
          loading={replace.isPending}
        >
          Save changes
        </Button>
      }
    >
      <SettingsSection
        title="Tracked competitors"
        description={
          <>
            A competitor can run several accounts - add each one. Their winners show on{' '}
            <RouterLink to="/competitors" className="text-text-brand underline">
              Competitors
            </RouterLink>{' '}
            and Home.
          </>
        }
      >
        <UsageMeter
          label="Competitor accounts"
          used={used}
          limit={limit}
          caption={
            full
              ? 'Your plan’s limit.'
              : `Your plan tracks up to ${limit} accounts across your competitors.`
          }
          action={
            <RouterLink
              to="/settings/billing"
              className="text-caption text-text-brand underline"
            >
              Upgrade
            </RouterLink>
          }
        />
        <SettingsCard>
          <ul aria-label="Competitors">
            {fields.map((field, i) => {
              const saved = savedFor(form.getValues(`competitors.${i}.id`));
              const name = `Competitor ${i + 1}`;
              /** The saved account a row shows, by platform and handle. */
              const savedAccount = (platform: string, handle: string) => {
                const bare = handle.trim().replace(/^@/, '').toLowerCase();
                return saved?.accounts.find(
                  (a) => a.platform === platform && a.handle === bare,
                );
              };
              return (
                <li
                  key={field.id}
                  className="border-border-subtle border-b py-5 last:border-b-0"
                >
                  <Stack gap={3}>
                    <Inline gap={2} alignY="top">
                      <Controller
                        name={`competitors.${i}.website`}
                        control={form.control}
                        render={({ field: input, fieldState }) => (
                          <Field
                            label={name}
                            optional
                            error={fieldState.error?.message}
                            className="flex-1"
                          >
                            <SiteInput
                              {...input}
                              type="url"
                              inputMode="url"
                              placeholder="oatly.com"
                            />
                          </Field>
                        )}
                      />
                      {saved && saved.accounts.length > 0 && (
                        <IconButton
                          type="button"
                          icon={ArrowsClockwise}
                          variant="ghost"
                          aria-label={`Check ${saved.label} again now`}
                          className="mt-7"
                          onClick={() =>
                            void refresh
                              .mutateAsync({ id: workspaceId, competitorId: saved.id })
                              .then(() =>
                                toast(
                                  'Checking again - new posts show up in a few minutes',
                                ),
                              )
                              .catch(() => undefined)
                          }
                        />
                      )}
                      <IconButton
                        type="button"
                        icon={Trash}
                        variant="ghost"
                        aria-label={`Remove ${name.toLowerCase()}`}
                        className="mt-7"
                        onClick={() => remove(i)}
                      />
                    </Inline>
                    <AccountsEditor
                      control={form.control}
                      setValue={form.setValue}
                      index={i}
                      name={name}
                      canAdd={!full}
                      status={(account) => {
                        const match = savedAccount(account.platform, account.handle);
                        if (!match) return null;
                        const status = accountStatus(match);
                        return (
                          <p className={`text-caption ${TONE[status.tone]}`}>
                            {status.text}
                          </p>
                        );
                      }}
                    />
                  </Stack>
                </li>
              );
            })}
          </ul>
          <Inline className="py-4">
            <Button
              type="button"
              variant="outline"
              leadingIcon={Plus}
              onClick={() => append(blankCompetitor(), { shouldFocus: false })}
            >
              Add competitor
            </Button>
          </Inline>
        </SettingsCard>
      </SettingsSection>
    </SettingsPageLayout>
  );
}
